// Mockup-mode fixture for the Day-Diary v2 step debugger.
//
// The real step debugger reads org events + makes Anthropic calls; neither
// works in mockup mode. To preview the extraction-summary panel without
// touching the backend, `useDebugSession` short-circuits to this fixture
// when `mockupMode` is true. The fixture mirrors the shape the runner
// produces post-success: one fan-out, a couple of project consolidates,
// one fallback consolidate (with unattributable + personal-placeholder +
// new_activity rows so every panel branch renders), and a done step.
//
// The activity ids match the user's pasted scenario for visual continuity
// (`cmp7f6qoq00f40to2qfcc7w3x` Terraform, `cmp7f5u9y00eq0to2qrxflef8` VWO).

import type {
  DebugSessionDto,
  DebugStep,
  EventCategorization,
  EventMeta,
  VisionVerdictSummary,
} from '@/hooks/useDebugSession';

// ── Synthetic event metadata ───────────────────────────────────────────────
//
// In mockup mode the page never hits the backend, so the events the panel
// drills into below each suggestion are fabricated here. Every event id
// referenced by the suggestion / entry rows above MUST appear in the
// matching step's `events` lookup, otherwise the per-suggestion expand
// renders 0 detail rows.

// Canned categorizations + vision verdicts for the mockup. Realistic
// enough to exercise every panel branch (enrich / attribute / skipped /
// no-vision; rule / fallback / manual classification sources; project /
// Internal current categorization).

const TF_CATEGORIZATION: EventCategorization = {
  source: 'rule',
  projectName: 'Shared Infrastructure',
  clientName: 'ClickWorx',
};

const SLACK_CATEGORIZATION: EventCategorization = {
  source: 'rule',
  projectName: 'Internal Communication',
  clientName: 'ClickWorx',
};

const VWO_CATEGORIZATION: EventCategorization = {
  source: 'rule',
  projectName: 'Contrix.ClientSites',
  clientName: 'Contrix',
};

const INTERNAL_BROWSING_CATEGORIZATION: EventCategorization = {
  source: 'fallback',
  projectName: '_Browsing',
  clientName: 'Internal',
};

const visionEnrichConfirms = (refined: string, tokens: string[]): VisionVerdictSummary => ({
  kind: 'enrich',
  verdict: 'confirms',
  reason: null,
  observed: 'Active window shows the expected file/page content',
  observedText: tokens,
  refinedDescription: refined,
  evidence: null,
  confidence: 0.88,
  errorMessage: null,
});

const visionEnrichPartial = (refined: string, tokens: string[]): VisionVerdictSummary => ({
  kind: 'enrich',
  verdict: 'partial',
  reason: 'Description was largely correct but missing the specific file context',
  observed: 'VS Code editor focused on a Terraform variables file',
  observedText: tokens,
  refinedDescription: refined,
  evidence: null,
  confidence: 0.72,
  errorMessage: null,
});

const visionAttribute = (
  activityName: string,
  tokens: string[],
  evidence: string,
): VisionVerdictSummary => ({
  kind: 'attribute',
  verdict: 'existing',
  reason: null,
  observed: 'Browser tab on a research-related page',
  observedText: tokens,
  refinedDescription: null,
  evidence: `Strong signal toward "${activityName}": ${evidence}`,
  confidence: 0.81,
  errorMessage: null,
});

const visionSkippedNoBytes: VisionVerdictSummary = {
  kind: 'skipped',
  verdict: 'no_signal',
  reason: 'no_bytes',
  observed: null,
  observedText: [],
  refinedDescription: null,
  evidence: null,
  confidence: null,
  errorMessage: null,
};

const visionSkippedRedacted: VisionVerdictSummary = {
  kind: 'skipped',
  verdict: 'no_signal',
  reason: 'redacted',
  observed: null,
  observedText: [],
  refinedDescription: null,
  evidence: null,
  confidence: null,
  errorMessage: null,
};

function makeEvent(input: {
  id: string;
  occurredAt: string;
  app: string;
  title: string | null;
  url?: string | null;
  screenshotId?: string | null;
  visionVerdict?: VisionVerdictSummary | null;
  categorization?: EventCategorization | null;
}): EventMeta {
  return {
    id: input.id,
    occurredAt: input.occurredAt,
    app: input.app,
    title: input.title,
    url: input.url ?? null,
    screenshotId: input.screenshotId ?? null,
    visionVerdict: input.visionVerdict ?? null,
    categorization: input.categorization ?? null,
  };
}

const TF_EVENTS_RAW: EventMeta[] = [
  // Continued Terraform backup-vault-plan configuration and architecture planning — 6
  makeEvent({
    id: 'e-tf-1',
    occurredAt: '2026-05-29T15:56:00Z',
    app: 'VS Code',
    title: 'source.cfg.example — backup-vault-plan',
    screenshotId: 'mockup-screenshot-1',
    visionVerdict: visionEnrichConfirms(
      'Editing source.cfg.example with AWS credentials and Terraform state bucket configuration for backup-vault-plan',
      ['STATE_BUCKET', 'AWS_ACCESS_KEY_ID', 'backup-vault-plan', 'source.cfg.example'],
    ),
  }),
  makeEvent({
    id: 'e-tf-2',
    occurredAt: '2026-05-29T15:59:00Z',
    app: 'VS Code',
    title: 'main.tf — backup-vault-plan',
    screenshotId: 'mockup-screenshot-2',
    visionVerdict: visionEnrichConfirms(
      'Editing main.tf — AWS Backup vault resource definitions for the LAGV unified vault',
      ['aws_backup_vault', 'logically_air_gapped', 'main.tf', 'recovery_org'],
    ),
  }),
  makeEvent({
    id: 'e-tf-3',
    occurredAt: '2026-05-29T16:03:00Z',
    app: 'VS Code',
    title: 'variables.tf — backup-vault-plan',
    screenshotId: 'mockup-screenshot-3',
    visionVerdict: visionEnrichPartial(
      'Editing variables.tf — declaring the vault_name and retention_days variables consumed by main.tf',
      ['vault_name', 'retention_days', 'variables.tf', 'TF_VAR_'],
    ),
  }),
  makeEvent({
    id: 'e-tf-4',
    occurredAt: '2026-05-29T16:07:00Z',
    app: 'Slack',
    title: '#aws-security — backup vault discussion',
    url: 'https://clickworx.slack.com/archives/aws-security',
    screenshotId: 'mockup-screenshot-4',
  }),
  makeEvent({
    id: 'e-tf-5',
    occurredAt: '2026-05-29T16:11:00Z',
    app: 'VS Code',
    title: 'providers.tf — backup-vault-plan',
    screenshotId: 'mockup-screenshot-5',
  }),
  makeEvent({
    id: 'e-tf-6',
    occurredAt: '2026-05-29T16:16:00Z',
    app: 'VS Code',
    title: 'outputs.tf — backup-vault-plan',
    screenshotId: 'mockup-screenshot-6',
  }),
  // Editing Terraform backup-vault-plan configuration in VS Code — 5
  makeEvent({
    id: 'e-tf-7',
    occurredAt: '2026-05-29T15:15:00Z',
    app: 'VS Code',
    title: 'source.cfg.example — backup-vault-plan',
    screenshotId: 'mockup-screenshot-7',
    visionVerdict: visionSkippedNoBytes,
  }),
  makeEvent({
    id: 'e-tf-8',
    occurredAt: '2026-05-29T15:19:00Z',
    app: 'VS Code',
    title: 'main.tf — backup-vault-plan',
    screenshotId: 'mockup-screenshot-8',
  }),
  makeEvent({
    id: 'e-tf-9',
    occurredAt: '2026-05-29T15:22:00Z',
    app: 'VS Code',
    title: 'terraform.tfvars — backup-vault-plan',
    screenshotId: 'mockup-screenshot-9',
  }),
  makeEvent({
    id: 'e-tf-10',
    occurredAt: '2026-05-29T15:25:00Z',
    app: 'VS Code',
    title: 'README.md — backup-vault-plan',
    screenshotId: 'mockup-screenshot-10',
  }),
  makeEvent({
    id: 'e-tf-11',
    occurredAt: '2026-05-29T15:28:00Z',
    app: 'VS Code',
    title: 'main.tf — backup-vault-plan',
    screenshotId: 'mockup-screenshot-11',
  }),
  // Reviewing ci.yml diff in terraform-templates repository — 2
  makeEvent({
    id: 'e-tf-12',
    occurredAt: '2026-05-29T18:17:00Z',
    app: 'VS Code',
    title: 'ci.yml (eff7c49) — terraform-templates',
    screenshotId: 'mockup-screenshot-12',
  }),
  makeEvent({
    id: 'e-tf-13',
    occurredAt: '2026-05-29T18:30:00Z',
    app: 'VS Code',
    title: 'ci.yml (060a3f5) — terraform-templates',
    screenshotId: 'mockup-screenshot-13',
  }),
  // Continued review of terraform-templates ci.yml diff with PR discussion — 2
  makeEvent({
    id: 'e-tf-14',
    occurredAt: '2026-05-29T20:44:00Z',
    app: 'Google Chrome',
    title: 'PR #9 · terraform-templates · GitHub',
    url: 'https://github.com/clickworx/terraform-templates/pull/9',
    screenshotId: 'mockup-screenshot-14',
    visionVerdict: visionAttribute(
      'Terraform & AWS Infrastructure Setup',
      ['terraform-templates', 'ci.yml', 'pull/9', 'merge'],
      'GitHub PR diff on the terraform-templates repo discussing CI pipeline changes',
    ),
  }),
  makeEvent({
    id: 'e-tf-15',
    occurredAt: '2026-05-29T20:46:00Z',
    app: 'VS Code',
    title: 'ci.yml — terraform-templates',
    screenshotId: 'mockup-screenshot-15',
  }),
  // Continuing terraform-templates ci.yml diff review with cross-org backup discussion — 2
  makeEvent({
    id: 'e-tf-16',
    occurredAt: '2026-05-29T21:42:00Z',
    app: 'Slack',
    title: '#aws-security — cross-org backup replication',
    url: 'https://clickworx.slack.com/archives/aws-security/p1748550120',
    screenshotId: 'mockup-screenshot-16',
  }),
  makeEvent({
    id: 'e-tf-17',
    occurredAt: '2026-05-29T21:44:00Z',
    app: 'VS Code',
    title: 'ci.yml — terraform-templates',
    screenshotId: 'mockup-screenshot-17',
  }),
  // Reading AWS Backup documentation — 1
  makeEvent({
    id: 'e-tf-18',
    occurredAt: '2026-05-29T09:30:00Z',
    app: 'Google Chrome',
    title: 'Logically air-gapped vault — AWS Backup',
    url: 'https://docs.aws.amazon.com/aws-backup/latest/devguide/logicallyairgappedvault.html',
    screenshotId: 'mockup-screenshot-18',
    visionVerdict: visionAttribute(
      'Terraform & AWS Infrastructure Setup',
      ['Logically air-gapped vault', 'AWS Backup', 'Vault Lock', 'Backup policies'],
      'AWS docs page on logically air-gapped vault — research for the LAGV terraform configuration',
    ),
  }),
  // Monitoring AWS alerts in Slack — 1
  makeEvent({
    id: 'e-tf-19',
    occurredAt: '2026-05-29T15:45:00Z',
    app: 'Slack',
    title: '#aws-alerts — ClickWorx',
    url: 'https://clickworx.slack.com/archives/aws-alerts',
    screenshotId: 'mockup-screenshot-19',
  }),
  // Reviewing Slack threads about Terraform CI pipeline configuration — 1
  makeEvent({
    id: 'e-tf-20',
    occurredAt: '2026-05-29T19:00:00Z',
    app: 'Slack',
    title: 'Threads — ClickWorx',
    url: 'https://clickworx.slack.com/threads',
    screenshotId: 'mockup-screenshot-20',
  }),
];

const VWO_EVENTS_RAW: EventMeta[] = [
  // Reviewing VWO report for (welcome) mag-subscribe mobile test #40 — 2
  makeEvent({
    id: 'e-vwo-1',
    occurredAt: '2026-05-29T21:47:00Z',
    app: 'Google Chrome',
    title: '(welcome) mag-subscribe: mobile · Report · VWO',
    url: 'https://app.vwo.com/split/40/report',
    screenshotId: 'mockup-screenshot-21',
    visionVerdict: visionAttribute(
      'VWO mag-subscribe testing',
      ['mag-subscribe: mobile', 'split/40', 'Sales', 'Variation 1'],
      'VWO report page for split test #40 on the mag-subscribe mobile experience',
    ),
  }),
  makeEvent({
    id: 'e-vwo-2',
    occurredAt: '2026-05-29T21:48:00Z',
    app: 'Google Chrome',
    title: '(welcome) mag-subscribe: mobile · Report · VWO',
    url: 'https://app.vwo.com/split/40/report',
    screenshotId: 'mockup-screenshot-22',
  }),
  // Reviewing VWO A/B test report for mag-subscribe desktop/tablet — 1
  makeEvent({
    id: 'e-vwo-3',
    occurredAt: '2026-05-29T11:00:00Z',
    app: 'Google Chrome',
    title: 'mag-subscribe: desktop/tablet · Report · VWO',
    url: 'https://app.vwo.com/split/38/report',
    screenshotId: 'mockup-screenshot-23',
    visionVerdict: visionSkippedRedacted,
  }),
  // Configuring VWO targeting for mag-subscribe mobile split test — 1
  makeEvent({
    id: 'e-vwo-4',
    occurredAt: '2026-05-29T12:20:00Z',
    app: 'Google Chrome',
    title: '(welcome) mag-subscribe: mobile · Targeting · VWO',
    url: 'https://app.vwo.com/split/40/configure',
    screenshotId: 'mockup-screenshot-24',
  }),
  // Reviewing VWO report for (welcome) mag-subscribe desktop/tablet test #41 — 1
  makeEvent({
    id: 'e-vwo-5',
    occurredAt: '2026-05-29T14:10:00Z',
    app: 'Google Chrome',
    title: '(welcome) mag-subscribe: desktop/tablet · Report · VWO',
    url: 'https://app.vwo.com/split/41/report',
    screenshotId: 'mockup-screenshot-25',
  }),
  // Reviewing VWO split test campaigns list — 1
  makeEvent({
    id: 'e-vwo-6',
    occurredAt: '2026-05-29T19:35:00Z',
    app: 'Google Chrome',
    title: 'Split URL Tests · VWO',
    url: 'https://app.vwo.com/campaigns',
    screenshotId: 'mockup-screenshot-26',
  }),
];

const FB_EVENTS_RAW: EventMeta[] = [
  // Windows Explorer — unattributable
  makeEvent({
    id: 'e-fb-1',
    occurredAt: '2026-05-29T10:15:00Z',
    app: 'Windows Explorer',
    title: null,
  }),
  // Researching SaaS pricing pages — 2
  makeEvent({
    id: 'e-fb-2',
    occurredAt: '2026-05-29T17:10:00Z',
    app: 'Google Chrome',
    title: 'Pricing — Linear',
    url: 'https://linear.app/pricing',
    screenshotId: 'mockup-screenshot-27',
    visionVerdict: {
      kind: 'attribute',
      verdict: 'new',
      reason: null,
      observed: 'Browser tab on the Linear pricing page with feature comparison',
      observedText: ['Linear', 'pricing', 'Standard', 'Plus', 'Per user / month'],
      refinedDescription: null,
      evidence:
        'Pricing-page research session on a SaaS vendor — proposes a new "SaaS pricing research" activity',
      confidence: 0.68,
      errorMessage: null,
    },
  }),
  makeEvent({
    id: 'e-fb-3',
    occurredAt: '2026-05-29T17:18:00Z',
    app: 'Google Chrome',
    title: 'Pricing — Notion',
    url: 'https://www.notion.so/pricing',
    screenshotId: 'mockup-screenshot-28',
  }),
  // Personal browsing — 4
  makeEvent({
    id: 'e-fb-4',
    occurredAt: '2026-05-29T08:05:00Z',
    app: 'Google Chrome',
    title: 'Hacker News',
    url: 'https://news.ycombinator.com/',
    screenshotId: 'mockup-screenshot-29',
    visionVerdict: visionSkippedRedacted,
  }),
  makeEvent({
    id: 'e-fb-5',
    occurredAt: '2026-05-29T08:12:00Z',
    app: 'Google Chrome',
    title: 'r/programming — reddit',
    url: 'https://www.reddit.com/r/programming/',
    screenshotId: 'mockup-screenshot-30',
  }),
  makeEvent({
    id: 'e-fb-6',
    occurredAt: '2026-05-29T12:35:00Z',
    app: 'Google Chrome',
    title: 'The Verge',
    url: 'https://www.theverge.com/',
    screenshotId: 'mockup-screenshot-31',
  }),
  makeEvent({
    id: 'e-fb-7',
    occurredAt: '2026-05-29T17:50:00Z',
    app: 'Google Chrome',
    title: 'Twitter',
    url: 'https://twitter.com/home',
    screenshotId: 'mockup-screenshot-32',
  }),
];

/**
 * Apply a default categorization to every event in the batch unless one is
 * already set. `perAppOverrides` lets a batch pin specific apps to a
 * different categorization (e.g. Slack inside a Terraform batch is still
 * an Internal Communication classification, not Shared Infrastructure).
 */
function withDefaultCategorization(
  events: readonly EventMeta[],
  defaultCat: EventCategorization,
  perAppOverrides: Record<string, EventCategorization> = {},
): EventMeta[] {
  return events.map((e) => ({
    ...e,
    categorization: e.categorization ?? perAppOverrides[e.app] ?? defaultCat,
  }));
}

const TF_EVENTS = withDefaultCategorization(TF_EVENTS_RAW, TF_CATEGORIZATION, {
  Slack: SLACK_CATEGORIZATION,
});
const VWO_EVENTS = withDefaultCategorization(VWO_EVENTS_RAW, VWO_CATEGORIZATION);
const FB_EVENTS = withDefaultCategorization(FB_EVENTS_RAW, INTERNAL_BROWSING_CATEGORIZATION);

function eventsById(events: readonly EventMeta[]): Record<string, EventMeta> {
  const out: Record<string, EventMeta> = {};
  for (const e of events) out[e.id] = e;
  return out;
}

const ACTIVITY_TERRAFORM = 'cmp7f6qoq00f40to2qfcc7w3x';
const ACTIVITY_VWO = 'cmp7f5u9y00eq0to2qrxflef8';
const ACTIVITY_PERSONAL = 'cmp7fpersonal0000to2person0000';

const TERRAFORM_ENTRIES = [
  {
    title: 'Continued Terraform backup-vault-plan configuration and architecture planning',
    description: '',
    startsAt: '2026-05-29T15:56:00Z',
    endsAt: '2026-05-29T16:16:00Z',
    eventIds: ['e-tf-1', 'e-tf-2', 'e-tf-3', 'e-tf-4', 'e-tf-5', 'e-tf-6'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.92,
    visionApplied: false,
  },
  {
    title: 'Editing Terraform backup-vault-plan configuration in VS Code',
    description: '',
    startsAt: '2026-05-29T15:15:00Z',
    endsAt: '2026-05-29T15:28:00Z',
    eventIds: ['e-tf-7', 'e-tf-8', 'e-tf-9', 'e-tf-10', 'e-tf-11'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.93,
    visionApplied: false,
  },
  {
    title: 'Reviewing ci.yml diff in terraform-templates repository',
    description: '',
    startsAt: '2026-05-29T18:17:00Z',
    endsAt: '2026-05-29T18:30:00Z',
    eventIds: ['e-tf-12', 'e-tf-13'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.93,
    visionApplied: false,
  },
  {
    title: 'Continued review of terraform-templates ci.yml diff with PR discussion',
    description: '',
    startsAt: '2026-05-29T20:44:00Z',
    endsAt: '2026-05-29T20:46:00Z',
    eventIds: ['e-tf-14', 'e-tf-15'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.93,
    visionApplied: false,
  },
  {
    title: 'Continuing terraform-templates ci.yml diff review with cross-org backup discussion',
    description: '',
    startsAt: '2026-05-29T21:42:00Z',
    endsAt: '2026-05-29T21:44:00Z',
    eventIds: ['e-tf-16', 'e-tf-17'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.92,
    visionApplied: false,
  },
  {
    title: 'Reading AWS Backup documentation on logically air-gapped vaults',
    description: '',
    startsAt: '2026-05-29T09:30:00Z',
    endsAt: '2026-05-29T09:30:00Z',
    eventIds: ['e-tf-18'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.82,
    visionApplied: false,
  },
  {
    title: 'Monitoring AWS alerts in Slack — backup and deployment notifications',
    description: '',
    startsAt: '2026-05-29T15:45:00Z',
    endsAt: '2026-05-29T15:45:00Z',
    eventIds: ['e-tf-19'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.82,
    visionApplied: false,
  },
  {
    title: 'Reviewing Slack threads about Terraform CI pipeline configuration',
    description: '',
    startsAt: '2026-05-29T19:00:00Z',
    endsAt: '2026-05-29T19:00:00Z',
    eventIds: ['e-tf-20'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_TERRAFORM },
    confidence: 0.82,
    visionApplied: false,
  },
];

const VWO_ENTRIES = [
  {
    title: 'Reviewing VWO report for (welcome) mag-subscribe mobile test #40',
    description: '',
    startsAt: '2026-05-29T21:47:00Z',
    endsAt: '2026-05-29T21:48:00Z',
    eventIds: ['e-vwo-1', 'e-vwo-2'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_VWO },
    confidence: 0.75,
    visionApplied: false,
  },
  {
    title: 'Reviewing VWO A/B test report for mag-subscribe desktop/tablet',
    description: '',
    startsAt: '2026-05-29T11:00:00Z',
    endsAt: '2026-05-29T11:00:00Z',
    eventIds: ['e-vwo-3'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_VWO },
    confidence: 0.82,
    visionApplied: false,
  },
  {
    title: 'Configuring VWO targeting for mag-subscribe mobile split test',
    description: '',
    startsAt: '2026-05-29T12:20:00Z',
    endsAt: '2026-05-29T12:20:00Z',
    eventIds: ['e-vwo-4'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_VWO },
    confidence: 0.78,
    visionApplied: false,
  },
  {
    title: 'Reviewing VWO report for (welcome) mag-subscribe desktop/tablet test #41',
    description: '',
    startsAt: '2026-05-29T14:10:00Z',
    endsAt: '2026-05-29T14:10:00Z',
    eventIds: ['e-vwo-5'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_VWO },
    confidence: 0.72,
    visionApplied: false,
  },
  {
    title: 'Reviewing VWO split test campaigns list for mag-subscribe tests',
    description: '',
    startsAt: '2026-05-29T19:35:00Z',
    endsAt: '2026-05-29T19:35:00Z',
    eventIds: ['e-vwo-6'],
    activityRef: { kind: 'existing' as const, id: ACTIVITY_VWO },
    confidence: 0.72,
    visionApplied: false,
  },
];

const FALLBACK_SUGGESTIONS = [
  {
    eventIds: ['e-fb-1'],
    kind: 'unattributable' as const,
    activityRef: null,
    title: 'Windows Explorer — no identifiable content',
    description: '',
    confidence: 0.1,
    evidence: '',
    observedText: [],
  },
  {
    eventIds: ['e-fb-2', 'e-fb-3'],
    kind: 'new_activity' as const,
    activityRef: {
      kind: 'new' as const,
      name: 'Researching SaaS pricing pages',
      description: 'Comparing competitor pricing tiers across multiple SaaS sites',
    },
    title: 'Researching SaaS pricing pages',
    description: '',
    confidence: 0.65,
    evidence: '',
    observedText: [],
  },
  {
    eventIds: ['e-fb-4', 'e-fb-5', 'e-fb-6', 'e-fb-7'],
    kind: 'personal' as const,
    activityRef: { kind: 'personal-placeholder' as const },
    title: 'Personal browsing — news and social',
    description: '',
    confidence: 0.7,
    evidence: '',
    observedText: [],
  },
];

const STEPS: DebugStep[] = [
  {
    index: 0,
    kind: 'fan-out',
    label: '3 batches across 2 projects + 1 fallback',
    parentIndex: null,
    prompt: null,
    response: null,
    fanOut: [
      { kind: 'text-pass.project', label: 'batch 1/3 • Shared Infrastructure' },
      { kind: 'text-pass.project', label: 'batch 2/3 • Contrix.ClientSites' },
      { kind: 'text-pass.fallback', label: 'batch 3/3 • fallback' },
    ],
    error: null,
    summaryContext: null,
  },
  {
    index: 1,
    kind: 'text-pass.project',
    label: 'text-pass • Shared Infrastructure • batch 1/3',
    parentIndex: 0,
    prompt: { system: null, user: '(mockup prompt)', model: 'claude-sonnet-4-6' },
    response: {
      raw: '{"entries":[]}',
      parsed: { entries: TERRAFORM_ENTRIES, enrichmentRequests: [] },
      inputTokens: 4_200,
      outputTokens: 1_100,
      elapsedMs: 8_400,
    },
    fanOut: [{ kind: 'consolidate.project', label: 'consolidate • Shared Infrastructure' }],
    error: null,
    summaryContext: null,
  },
  {
    index: 2,
    kind: 'consolidate.project',
    label: 'consolidate • Shared Infrastructure • batch 1/3',
    parentIndex: 1,
    prompt: { system: null, user: '(mockup prompt)', model: 'claude-sonnet-4-6' },
    response: {
      raw: '{"entries":[…]}',
      parsed: { entries: TERRAFORM_ENTRIES },
      inputTokens: 5_100,
      outputTokens: 1_650,
      elapsedMs: 12_300,
    },
    fanOut: [],
    error: null,
    summaryContext: {
      kind: 'consolidate.project',
      batchProject: {
        id: 'p-shared',
        name: 'Shared Infrastructure',
        clientName: 'ClickWorx',
      },
      activityLookup: {
        [ACTIVITY_TERRAFORM]: {
          activityName: 'Terraform & AWS Infrastructure Setup',
          projectName: 'Shared Infrastructure',
          clientName: 'ClickWorx',
          isPersonal: false,
        },
      },
      events: eventsById(TF_EVENTS),
      personalActivityExists: false,
    },
  },
  {
    index: 3,
    kind: 'text-pass.project',
    label: 'text-pass • Contrix.ClientSites • batch 2/3',
    parentIndex: 0,
    prompt: { system: null, user: '(mockup prompt)', model: 'claude-sonnet-4-6' },
    response: {
      raw: '{"entries":[…]}',
      parsed: { entries: VWO_ENTRIES, enrichmentRequests: [] },
      inputTokens: 2_800,
      outputTokens: 760,
      elapsedMs: 5_900,
    },
    fanOut: [{ kind: 'consolidate.project', label: 'consolidate • Contrix.ClientSites' }],
    error: null,
    summaryContext: null,
  },
  {
    index: 4,
    kind: 'consolidate.project',
    label: 'consolidate • Contrix.ClientSites • batch 2/3',
    parentIndex: 3,
    prompt: { system: null, user: '(mockup prompt)', model: 'claude-sonnet-4-6' },
    response: {
      raw: '{"entries":[…]}',
      parsed: { entries: VWO_ENTRIES },
      inputTokens: 3_300,
      outputTokens: 980,
      elapsedMs: 8_100,
    },
    fanOut: [],
    error: null,
    summaryContext: {
      kind: 'consolidate.project',
      batchProject: {
        id: 'p-clientsites',
        name: 'Contrix.ClientSites',
        clientName: 'Contrix',
      },
      activityLookup: {
        [ACTIVITY_VWO]: {
          activityName: 'VWO mag-subscribe testing',
          projectName: 'Contrix.ClientSites',
          clientName: 'Contrix',
          isPersonal: false,
        },
      },
      events: eventsById(VWO_EVENTS),
      personalActivityExists: false,
    },
  },
  {
    index: 5,
    kind: 'text-pass.fallback',
    label: 'text-pass (reclaim) • batch 3/3',
    parentIndex: 0,
    prompt: { system: null, user: '(mockup prompt)', model: 'claude-sonnet-4-6' },
    response: {
      raw: '{"hypotheses":[…]}',
      parsed: { hypotheses: [], enrichmentRequests: [] },
      inputTokens: 1_900,
      outputTokens: 540,
      elapsedMs: 4_200,
    },
    fanOut: [{ kind: 'consolidate.fallback', label: 'consolidate (reclaim) • batch 3/3' }],
    error: null,
    summaryContext: null,
  },
  {
    index: 6,
    kind: 'consolidate.fallback',
    label: 'consolidate (reclaim) • batch 3/3',
    parentIndex: 5,
    prompt: { system: null, user: '(mockup prompt)', model: 'claude-sonnet-4-6' },
    response: {
      raw: '{"suggestions":[…]}',
      parsed: { suggestions: FALLBACK_SUGGESTIONS },
      inputTokens: 2_600,
      outputTokens: 720,
      elapsedMs: 6_400,
    },
    fanOut: [],
    error: null,
    summaryContext: {
      kind: 'consolidate.fallback',
      batchProject: null,
      activityLookup: {
        [ACTIVITY_PERSONAL]: {
          activityName: 'Personal',
          projectName: 'Personal',
          clientName: 'Personal',
          isPersonal: true,
        },
      },
      events: eventsById(FB_EVENTS),
      personalActivityExists: false,
    },
  },
  {
    index: 7,
    kind: 'done',
    label: 'Pipeline complete',
    parentIndex: null,
    prompt: null,
    response: null,
    fanOut: [],
    error: null,
    summaryContext: null,
  },
];

/**
 * Build a fresh, deterministic mockup-mode session. Called by
 * `useDebugSession.start()` when `mockupMode` is true. Returns a new
 * object each call so React state setters see a referentially-new
 * session.
 */
export function buildMockupDebugSession(input: {
  orgId: string;
  employeeId: string;
  date: string;
}): DebugSessionDto {
  return {
    id: `mockup-${input.employeeId}-${input.date}`,
    orgId: input.orgId,
    employeeId: input.employeeId,
    date: input.date,
    createdAt: new Date('2026-05-30T08:00:00Z').toISOString(),
    lastActivityAt: new Date('2026-05-30T08:01:00Z').toISOString(),
    status: 'done',
    steps: STEPS,
  };
}
